import React, { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './PublicInfoPages.css';

const supportEmail = 'streaming@watchvim.com';
const advertisingEmail = 'advertise@watchvim.com';

function InfoShell({ eyebrow = 'WatchVIM', title, intro, children }) {
  return (
    <section className="public-info-page">
      <header className="public-info-hero">
        <div className="eyebrow">{eyebrow}</div>
        <h1>{title}</h1>
        {intro ? <p>{intro}</p> : null}
      </header>
      <div className="public-info-card">{children}</div>
    </section>
  );
}

function InfoLinks({ children }) {
  return <div className="public-info-actions">{children}</div>;
}

export function AboutPage() {
  return (
    <InfoShell
      eyebrow="About WatchVIM"
      title="Independent stories. Built for discovery."
      intro="WatchVIM is a streaming destination for independent films, series, shorts, originals, and storytellers whose work deserves a direct path to audiences."
    >
      <div className="public-info-document">
        <section className="public-info-owner-video" aria-labelledby="watchvim-owners-heading">
          <h2 id="watchvim-owners-heading">Meet the Owners of WatchVIM</h2>
          <p>Hear directly from the owners of WatchVIM about the people and vision behind the platform.</p>
          <div className="public-info-video-frame">
            <iframe
              src="https://www.youtube.com/embed/OM5ou7dggLo"
              title="Meet the Owners of WatchVIM"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </section>

        <h2>What you can watch</h2>
        <p>WatchVIM brings together feature films, episodic series, short-form programming, documentaries, and original productions in one streaming experience. Availability can vary by title, territory, licensing window, and viewing model.</p>

        <h2>How access works</h2>
        <p>Depending on the title, viewers may watch through subscription access, an individual rental or purchase, or an ad-supported viewing experience. The title page and checkout flow show the access method that applies before you watch or pay.</p>

        <h2>For filmmakers</h2>
        <p>WatchVIM also provides distribution and filmmaker tools for eligible independent projects. Filmmakers can submit work for review and, when accepted, use the filmmaker portal to manage participating titles and related activity.</p>

        <InfoLinks>
          <NavLink to="/movies">Explore Movies</NavLink>
          <NavLink to="/originals">WatchVIM Originals</NavLink>
          <NavLink to="/submit">Submit a Project</NavLink>
        </InfoLinks>
      </div>
    </InfoShell>
  );
}

export function FaqPage() {
  const items = [
    ['Do I need an account to browse WatchVIM?', 'No. You can browse public catalog pages without signing in. An account may be required to subscribe, rent, purchase, resume viewing, or access account-specific features.'],
    ['Why do some titles have ads?', 'Some titles are offered through an ad-supported viewing model. Subscription or transactional access may use different ad rules depending on the title and the access decision shown by WatchVIM.'],
    ['How do rentals and purchases work?', 'The checkout page shows the price and access terms available for the selected title. Once a transaction is completed, the title appears in the viewer’s eligible account access according to those terms.'],
    ['How do I cancel a subscription?', 'Use the membership or billing area associated with your WatchVIM account, or the platform where you originally subscribed when an app-store subscription is involved. Cancellation stops future renewal but does not automatically create a refund.'],
    ['What is WatchVIM’s refund policy?', 'Except where applicable law requires otherwise, WatchVIM digital transactions are final. Review the Refund Policy and Terms before completing a purchase.'],
    ['How can a filmmaker submit a project?', 'Use the Submit Film area to send an eligible film, series, short, or documentary for review. Submission does not guarantee acceptance or distribution.'],
    ['Where can I get technical help?', `Use the Support page or email ${supportEmail}. For playback issues, include the title, device, browser or app, and a brief description of what happened.`],
  ];

  return (
    <InfoShell eyebrow="WatchVIM Help" title="Frequently Asked Questions" intro="Answers to common questions about watching, paying, submitting, and getting support on WatchVIM.">
      <div className="public-info-document public-info-faq">
        {items.map(([question, answer]) => (
          <section key={question}>
            <h2>{question}</h2>
            <p>{answer}</p>
          </section>
        ))}
        <InfoLinks>
          <NavLink to="/support">Contact Support</NavLink>
          <NavLink to="/refund-policy">Refund Policy</NavLink>
          <NavLink to="/terms">Terms</NavLink>
        </InfoLinks>
      </div>
    </InfoShell>
  );
}

export function RefundPolicyPage() {
  return (
    <InfoShell eyebrow="WatchVIM Legal" title="Refund Policy" intro="This page summarizes WatchVIM’s current refund and cancellation rules. The Terms and Conditions remain the controlling agreement.">
      <div className="public-info-document">
        <p className="public-info-effective">Effective Date: August 21, 2026</p>

        <h2>Digital transactions are generally final</h2>
        <p>Except where required by applicable law, WatchVIM does not provide refunds or credits for subscription fees, TVOD rentals or purchases, or other completed digital transactions, including partially used billing periods.</p>

        <h2>Subscription cancellations</h2>
        <p>Canceling a subscription prevents future renewal. Access ordinarily continues through the end of the already-paid billing period. Cancellation does not create a refund or prorated credit for unused time unless applicable law requires otherwise.</p>

        <h2>Rentals and purchases</h2>
        <p>Completed rentals and purchases are final except where applicable law requires a remedy. A title not being watched, or being only partially watched, does not by itself create a refund right.</p>

        <h2>Technical issues</h2>
        <p>If a technical problem prevents playback, contact WatchVIM Support so the issue can be investigated. Any courtesy remedy offered outside a legal requirement is discretionary and does not change the general all-sales-final policy.</p>

        <h2>Legal rights</h2>
        <p>Nothing in this policy limits any mandatory consumer right that cannot legally be waived or restricted in the viewer’s jurisdiction.</p>

        <InfoLinks>
          <NavLink to="/terms">Read Full Terms</NavLink>
          <NavLink to="/support">Get Support</NavLink>
        </InfoLinks>
      </div>
    </InfoShell>
  );
}

export function AccessibilityPage() {
  return (
    <InfoShell eyebrow="WatchVIM Accessibility" title="Accessibility" intro="WatchVIM wants its streaming experience and public information to be usable by as many people as reasonably possible.">
      <div className="public-info-document">
        <h2>Our approach</h2>
        <p>We work to improve keyboard access, readable structure, focus visibility, responsive layouts, descriptive controls, and compatibility with common assistive technologies across the WatchVIM website.</p>

        <h2>Video accessibility</h2>
        <p>Caption, subtitle, and audio-description availability can vary by title and by the materials supplied or licensed for that title. When an accessibility track is available through the playback source, WatchVIM aims to expose it through the viewing experience.</p>

        <h2>Need assistance?</h2>
        <p>If you encounter an accessibility barrier, tell us what page or feature you were using, what device or assistive technology was involved, and what prevented access. We use that information to investigate and prioritize improvements.</p>

        <InfoLinks>
          <a href={`mailto:${supportEmail}?subject=WatchVIM%20Accessibility`}>Email Accessibility Feedback</a>
          <NavLink to="/support">Support</NavLink>
        </InfoLinks>
      </div>
    </InfoShell>
  );
}

export function DmcaPage() {
  return (
    <InfoShell eyebrow="WatchVIM Legal" title="Copyright & DMCA Notices" intro="WatchVIM respects intellectual-property rights and expects users, filmmakers, advertisers, and partners to do the same.">
      <div className="public-info-document">
        <p className="public-info-effective">Effective Date: August 21, 2026</p>

        <h2>Copyright concerns</h2>
        <p>If you believe material available through WatchVIM infringes a copyright you own or are authorized to enforce, you may send a written infringement notice to VIM Media, LLC.</p>

        <h2>What to include in a notice</h2>
        <ul>
          <li>Your physical or electronic signature, or the signature of a person authorized to act for the copyright owner.</li>
          <li>Identification of the copyrighted work claimed to have been infringed, or a representative list when multiple works are involved.</li>
          <li>Identification of the material claimed to be infringing and enough information for WatchVIM to locate it, such as the title name and page URL.</li>
          <li>Your name, mailing address, telephone number, and email address.</li>
          <li>A statement that you have a good-faith belief that the disputed use is not authorized by the copyright owner, its agent, or the law.</li>
          <li>A statement that the information in the notice is accurate and, under penalty of perjury, that you are the copyright owner or authorized to act on the owner’s behalf.</li>
        </ul>

        <h2>Where to send the notice</h2>
        <div className="public-info-address">
          <strong>VIM Media, LLC / WatchVIM</strong>
          <span>Attn: Copyright Notice</span>
          <span>1801 E Camelback Rd Ste 102-1305</span>
          <span>Phoenix, AZ 85016</span>
          <a href={`mailto:${supportEmail}?subject=Copyright%20Notice`}>{supportEmail}</a>
        </div>

        <h2>After a notice</h2>
        <p>WatchVIM may remove or restrict access to material while a claim is reviewed and may contact the relevant content provider. A person whose material is removed may contact WatchVIM for information about the applicable counter-notice process.</p>

        <p>Knowingly making material misrepresentations in an infringement notice or counter-notice may create legal liability. Consider obtaining legal advice if you are unsure whether a use is authorized.</p>
      </div>
    </InfoShell>
  );
}

export function PressPage() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Links normally use full document navigation. This also catches legacy
    // client-side links and history entries, preserving server HTTP status/SEO.
    // Vite alone has no serverless routes; use Vercel dev/preview for newsroom QA.
    if (!import.meta.env.DEV) window.location.replace(pathname);
  }, [pathname]);

  return (
    <InfoShell eyebrow="WatchVIM Press" title="WatchVIM Newsroom" intro="Official WatchVIM announcements, company news, and media information from VIM Media.">
      <div className="public-info-document">
        <p role="status">Opening the newsroom…</p>
        <InfoLinks>
          <a href={pathname}>Continue to the newsroom</a>
          <a href={`mailto:${supportEmail}?subject=WatchVIM%20Press%20Inquiry`}>Email Press Inquiry</a>
        </InfoLinks>
      </div>
    </InfoShell>
  );
}

export function PartnersPage() {
  return (
    <InfoShell eyebrow="WatchVIM Business" title="Partnerships" intro="WatchVIM works with filmmakers, rights holders, advertisers, sponsors, production partners, and organizations that want to reach its audience or expand independent-content access.">
      <div className="public-info-document">
        <h2>Content and distribution</h2>
        <p>Filmmakers and rights holders can submit eligible projects for distribution consideration. Acceptance, territories, revenue terms, platform availability, and promotional support depend on the applicable agreement.</p>

        <h2>Advertising and sponsorship</h2>
        <p>Brands and organizations can explore WatchVIM advertising and sponsorship opportunities through the dedicated Advertise area or email <a href={`mailto:${advertisingEmail}`}>${advertisingEmail}</a>.</p>

        <h2>Other partnerships</h2>
        <p>For institutional, production, festival, programming, or strategic partnership ideas, use WatchVIM Support and select the advertising or partnership category.</p>

        <InfoLinks>
          <NavLink to="/submit">Submit Content</NavLink>
          <NavLink to="/advertise">Advertise</NavLink>
          <a href={`mailto:${advertisingEmail}?subject=WatchVIM%20Advertising%20Inquiry`}>Email Advertising</a>
          <NavLink to="/support">Other Partnership Questions</NavLink>
        </InfoLinks>
      </div>
    </InfoShell>
  );
}

export function DistributionPage() {
  return (
    <InfoShell eyebrow="WatchVIM Filmmakers" title="Distribution" intro="WatchVIM provides a submission and distribution pathway for eligible independent films, series, shorts, and documentaries.">
      <div className="public-info-document">
        <h2>Start with a submission</h2>
        <p>Submitting a project lets the WatchVIM team review the title, rights information, materials, and fit for the platform. Submission does not guarantee acceptance or distribution.</p>

        <h2>After acceptance</h2>
        <p>Accepted projects move forward under the applicable filmmaker or distribution agreement. Rights, term, territories, revenue participation, delivery requirements, and platform availability are governed by that agreement rather than this informational page.</p>

        <h2>Filmmaker tools</h2>
        <p>Participating filmmakers can use WatchVIM’s filmmaker area for supported title, reporting, revenue, payout, message, and profile workflows available to their account.</p>

        <InfoLinks>
          <NavLink to="/submit">Submit a Project</NavLink>
          <a href="/filmmaker-services">Post-Production Services</a>
          <NavLink to="/filmmakers">Filmmaker Portal</NavLink>
          <NavLink to="/support">Distribution Questions</NavLink>
        </InfoLinks>
      </div>
    </InfoShell>
  );
}

